import { FC, useMemo } from 'react'

import { EDITOR_TOOLS } from '@editor/config/constants'
import { PointArray } from '@editor/interfaces/attribute'
import { CircleAnchorProps } from '@editor/interfaces/canvas'
import useProjectionPlane from '@editor/pages/projects/blueprint-editor/hooks/useProjectionPlane'
import { useAppSelector } from '@editor/store/app'
import { CircleAnchorV2 } from '@infraloop/common'

/**
 * World-space distance (in meters) an anchor is nudged toward the camera while a
 * projection plane is active, so it sits just in front of the plane.
 */
const PROJECTION_PLANE_OFFSET = 0.005

/**
 * Editor-side adapter over the shared {@link CircleAnchorV2}. The shared anchor knows
 * nothing about the editor's stores, so this injects the editor-only behaviours:
 * the user's label font-size setting (Redux), suppressing `onDown` while the Focus
 * tool is active (hover/enter/leave still fire), and the projection-plane nudge on
 * the blueprint surface. Keeps the `CircleAnchorProps` surface so call sites only
 * change their import.
 */
const EditorCircleAnchor: FC<CircleAnchorProps> = ({ point, onDown, ...props }) => {
  // Hooks
  const { projectionDirections } = useProjectionPlane()

  // Store
  const labelFontSize = useAppSelector((state) => state.toolUtil.core.labelFontSize)
  const selectedTool = useAppSelector((state) => state.editor.selectedTool)

  // Derived state — point nudged toward the camera while a projection plane is active
  const adjustedPoint = useMemo<PointArray>(() => {
    if (!projectionDirections || !point) return point

    const offset = projectionDirections.normal.clone().negate().multiplyScalar(PROJECTION_PLANE_OFFSET)
    return [point[0] + offset.x, point[1] + offset.y, point[2] + offset.z]
  }, [point, projectionDirections])

  return (
    <CircleAnchorV2
      {...props}
      point={adjustedPoint}
      labelFontSize={labelFontSize}
      onDown={selectedTool === EDITOR_TOOLS.FOCUS ? undefined : onDown}
    />
  )
}

export default EditorCircleAnchor
